
import { defineComponent } from "vue";
import { Button, Icon, NavBar, Toast } from "vant";
import { getHostType } from "@/utils/host";
import { FRONTEND_VERSION } from "@/frontendVersion";

interface InfoItem {
  label: string;
  value: string;
  code?: boolean;
}

interface InfoSection {
  title: string;
  icon: string;
  items: InfoItem[];
}

export default defineComponent({
  components: {
    [Button.name]: Button,
    [Icon.name]: Icon,
    [NavBar.name]: NavBar,
  },

  data() {
    return {
      now: new Date(),
      clockTimer: null as number | null,
      online: navigator.onLine,
      visibilityState: document.visibilityState,
      viewport: "",
      orientation: "",
      connectionType: "",
      downlink: "",
      roundTripTime: "",
      saveData: "",
      hostType: getHostType(),
    };
  },

  computed: {
    deviceModel(): string {
      return this.readStorage("device");
    },

    formattedCurrentTime(): string {
      const pad = (value: number) => String(value).padStart(2, "0");
      return [
        `${this.now.getFullYear()}-${pad(this.now.getMonth() + 1)}-${pad(
          this.now.getDate()
        )}`,
        `${pad(this.now.getHours())}:${pad(this.now.getMinutes())}:${pad(
          this.now.getSeconds()
        )}`,
      ].join(" ");
    },

    visibleSections(): InfoSection[] {
      const worker = this.getWorkerData();
      const companyCode = this.readStorage("company_code");
      const account =
        this.readStorage("loginname") ||
        [worker.name, companyCode].filter(Boolean).join("@");
      const connectionItems = this.cleanItems([
        { label: "网络状态", value: this.online ? "在线" : "离线" },
        { label: "连接类型", value: this.connectionType },
        {
          label: "下行速度",
          value: this.downlink ? `${this.downlink} Mbps` : "",
        },
        {
          label: "往返延迟",
          value: this.roundTripTime ? `${this.roundTripTime} ms` : "",
        },
        { label: "节省流量", value: this.saveData },
      ]);

      return [
        {
          title: "账号与会话",
          icon: "contact",
          items: this.cleanItems([
            { label: "账号", value: account, code: true },
            { label: "坐席昵称", value: worker.nickname || "" },
          ]),
        },
        {
          title: "客户端与设备",
          icon: "phone-o",
          items: this.cleanItems([
            { label: "手机型号", value: this.deviceModel },
            { label: "前端版本", value: FRONTEND_VERSION, code: true },
            { label: "appVersion", value: this.readStorage("appVersion"), code: true },
            { label: "SDK", value: this.readStorage("SDK"), code: true },
            { label: "os", value: this.readStorage("os"), code: true },
            { label: "device", value: this.readStorage("device"), code: true },
            { label: "mobile_type", value: this.readStorage("mobile_type"), code: true },
            { label: "mobile_id", value: this.readStorage("mobile_id"), code: true },
            { label: "Web 版本", value: this.readStorage("version"), code: true },
            { label: "宿主环境", value: this.hostType },
          ]),
        },
        {
          title: "网络状态",
          icon: "cluster-o",
          items: connectionItems,
        },
        {
          title: "运行环境",
          icon: "setting-o",
          items: this.cleanItems([
            { label: "页面状态", value: this.visibilityText },
            { label: "当前页面", value: window.location.href, code: true },
            { label: "屏幕尺寸", value: `${screen.width} × ${screen.height}` },
            { label: "可视区域", value: this.viewport },
            { label: "像素密度", value: String(window.devicePixelRatio || 1) },
            { label: "屏幕方向", value: this.orientation },
            { label: "时区", value: this.timeZone },
            { label: "系统语言", value: navigator.language || "" },
            { label: "Cookie", value: navigator.cookieEnabled ? "可用" : "不可用" },
            { label: "User Agent", value: navigator.userAgent, code: true },
          ]),
        },
      ].filter((section) => section.items.length > 0);
    },

    visibilityText(): string {
      const labels: Record<string, string> = {
        visible: "前台可见",
        hidden: "后台隐藏",
        prerender: "预渲染",
      };
      return labels[this.visibilityState] || this.visibilityState;
    },

    timeZone(): string {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      } catch (error) {
        return "";
      }
    },

  },

  mounted() {
    this.updateRuntimeInfo();
    this.clockTimer = window.setInterval(() => {
      this.now = new Date();
    }, 1000);

    window.addEventListener("online", this.updateRuntimeInfo);
    window.addEventListener("offline", this.updateRuntimeInfo);
    window.addEventListener("resize", this.updateRuntimeInfo);
    window.addEventListener("orientationchange", this.updateRuntimeInfo);
    document.addEventListener("visibilitychange", this.updateRuntimeInfo);

    const connection = this.getConnection();
    connection?.addEventListener?.("change", this.updateRuntimeInfo);
  },

  beforeUnmount() {
    if (this.clockTimer !== null) {
      window.clearInterval(this.clockTimer);
      this.clockTimer = null;
    }

    window.removeEventListener("online", this.updateRuntimeInfo);
    window.removeEventListener("offline", this.updateRuntimeInfo);
    window.removeEventListener("resize", this.updateRuntimeInfo);
    window.removeEventListener("orientationchange", this.updateRuntimeInfo);
    document.removeEventListener("visibilitychange", this.updateRuntimeInfo);

    const connection = this.getConnection();
    connection?.removeEventListener?.("change", this.updateRuntimeInfo);
  },

  methods: {
    goBack() {
      this.$router.push("/settings");
    },

    readStorage(key: string): string {
      return localStorage.getItem(key) || "";
    },

    getWorkerData(): Record<string, any> {
      try {
        return JSON.parse(this.readStorage("worker_data")) || {};
      } catch (error) {
        return {};
      }
    },

    cleanItems(items: InfoItem[]): InfoItem[] {
      return items.filter((item) => item.value !== "" && item.value != null);
    },

    getConnection(): any {
      const nav = navigator as any;
      return nav.connection || nav.mozConnection || nav.webkitConnection;
    },

    updateRuntimeInfo() {
      const connection = this.getConnection();
      this.online = navigator.onLine;
      this.visibilityState = document.visibilityState;
      this.viewport = `${window.innerWidth} × ${window.innerHeight}`;
      this.orientation =
        screen.orientation?.type ||
        (window.innerWidth >= window.innerHeight ? "横屏" : "竖屏");
      this.connectionType = connection?.effectiveType || connection?.type || "";
      this.downlink = connection?.downlink != null ? String(connection.downlink) : "";
      this.roundTripTime = connection?.rtt != null ? String(connection.rtt) : "";
      this.saveData =
        connection?.saveData == null ? "" : connection.saveData ? "已开启" : "未开启";
    },

    getCopyText(): string {
      const lines = [
        "云雀设备调试信息",
        `生成时间：${this.formattedCurrentTime}`,
      ];

      this.visibleSections.forEach((section) => {
        lines.push("", `【${section.title}】`);
        section.items.forEach((item) => {
          lines.push(`${item.label}：${item.value}`);
        });
      });

      return lines.join("\n");
    },

    async copyInfo() {
      const content = this.getCopyText();

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(content);
        } else {
          this.copyWithTextArea(content);
        }
        Toast.success("调试信息已复制");
      } catch (error) {
        try {
          this.copyWithTextArea(content);
          Toast.success("调试信息已复制");
        } catch (fallbackError) {
          Toast.fail("复制失败，请长按内容手动复制");
        }
      }
    },

    copyWithTextArea(content: string) {
      const textArea = document.createElement("textarea");
      textArea.value = content;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();

      if (!document.execCommand("copy")) {
        document.body.removeChild(textArea);
        throw new Error("Copy command failed");
      }

      document.body.removeChild(textArea);
    },
  },
});
